import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { watchlistAPI } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { RatingBottomSheet } from '../components';
import { HiOutlineVideoCamera, HiOutlineTrash, HiOutlineChevronRight, HiOutlineX, HiOutlineSearch, HiOutlineRefresh, HiOutlineShare } from 'react-icons/hi';
import toast from 'react-hot-toast';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w200';

function MobileSeries() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [dateSortDirection, setDateSortDirection] = useState('desc');
  const [alphabeticalSortDirection, setAlphabeticalSortDirection] = useState('asc');
  const [progressSortDirection, setProgressSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideCompleted, setHideCompleted] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncingCurrent, setSyncingCurrent] = useState(0);
  const [syncingTotal, setSyncingTotal] = useState(0);
  const [syncingTitle, setSyncingTitle] = useState('');
  const [eventSource, setEventSource] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    seriesId: null,
    event: null,
  });

  useEffect(() => {
    loadSeries();
  }, []);

  const loadSeries = async () => {
    setLoading(true);
    try {
      const response = await watchlistAPI.getTV();
      setSeries(response.data);
    } catch (error) {
      console.error('Error loading series:', error);
      toast.error(t('series.errorLoadingSeries'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClick = (id, e) => {
    e.stopPropagation();
    setConfirmModal({ isOpen: true, seriesId: id, event: e });
  };

  const removeSeries = async () => {
    if (!confirmModal.seriesId) return;
    
    try {
      await watchlistAPI.remove(confirmModal.seriesId);
      setSeries((prev) => prev.filter((s) => s.id !== confirmModal.seriesId));
      toast.success(t('series.seriesRemoved'));
      setConfirmModal({ isOpen: false, seriesId: null, event: null });
    } catch (error) {
      toast.error(t('series.errorRemoving'));
    }
  };

  const handleRate = async (showId, rating) => {
    try {
      await watchlistAPI.update(showId, { user_rating: rating });
      setSeries(prev => prev.map(s =>
        s.id === showId ? { ...s, user_rating: rating } : s
      ));
      toast.success(rating > 0 ? t('rating.saved') : t('rating.removed'));
    } catch (error) {
      toast.error(t('rating.error'));
    }
  };

  const getProgress = (show) => {
    if (!show.seasons || show.seasons.length === 0) return 0;

    let totalEpisodes = 0;
    let watchedEpisodes = 0;

    show.seasons.forEach((season) => {
      if (season.watched) {
        totalEpisodes += season.episodes?.length || 0;
        watchedEpisodes += season.episodes?.length || 0;
      } else if (season.episodes) {
        season.episodes.forEach((ep) => {
          totalEpisodes++;
          if (ep.watched) watchedEpisodes++;
        });
      }
    });

    return totalEpisodes === 0 ? 0 : Math.round((watchedEpisodes / totalEpisodes) * 100);
  };

  const fullyWatchedCount = series.filter(show => getProgress(show) === 100).length;

  const handleDateSort = () => {
    if (sortBy === 'date') {
      setDateSortDirection(dateSortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy('date');
      setDateSortDirection('desc');
    }
  };

  const handleAlphabeticalSort = () => {
    if (sortBy === 'alphabetical') {
      setAlphabeticalSortDirection(alphabeticalSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy('alphabetical');
      setAlphabeticalSortDirection('asc');
    }
  };

  const handleProgressSort = () => {
    if (sortBy === 'progress') {
      setProgressSortDirection(progressSortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy('progress');
      setProgressSortDirection('desc');
    }
  };

  const handleFilterToggle = () => {
    if (filter === 'all') {
      setFilter('inProgress');
    } else if (filter === 'inProgress') {
      setFilter('completed');
    } else {
      setFilter('all');
    }
  };

  const handleSortTypeToggle = () => {
    if (sortBy === 'date') {
      setSortBy('alphabetical');
    } else if (sortBy === 'alphabetical') {
      setSortBy('progress');
    } else {
      setSortBy('date');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleShareList = async () => {
    const rated = series.filter(s => s.user_rating > 0).sort((a, b) => b.user_rating - a.user_rating);
    const completed = series.filter(s => getProgress(s) === 100 && !s.user_rating);
    const inProgress = series.filter(s => {
      const p = getProgress(s);
      return p > 0 && p < 100 && !s.user_rating;
    });
    const notStarted = series.filter(s => getProgress(s) === 0 && !s.user_rating);

    let text = `📺 ${t('series.mySeries')} — My Series\n`;
    text += `📊 ${series.length} ${t('share.seriesInList')} • ${fullyWatchedCount} ${t('share.completedLabel')}\n`;

    if (rated.length > 0) {
      text += `\n⭐ ${t('share.myRatings')}:\n`;
      rated.forEach(s => {
        const progress = getProgress(s);
        text += `★ ${s.user_rating} — ${s.title} (${progress}%)\n`;
      });
    }
    if (completed.length > 0) {
      text += `\n✅ ${t('share.completed')}:\n`;
      completed.forEach(s => { text += `• ${s.title}\n`; });
    }
    if (inProgress.length > 0) {
      text += `\n📺 ${t('share.inProgress')}:\n`;
      inProgress.forEach(s => {
        text += `• ${s.title} — ${getProgress(s)}%\n`;
      });
    }
    if (notStarted.length > 0) {
      text += `\n🎯 ${t('share.wantToWatch')}:\n`;
      notStarted.forEach(s => { text += `• ${s.title}\n`; });
    }

    const copyToClipboard = (value) => {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(t('share.copied'));
    };

    try {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (navigator.share && isMobile) {
        await navigator.share({ title: t('series.mySeries'), text });
      } else if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(t('share.copied'));
      } else {
        copyToClipboard(text);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          copyToClipboard(text);
        } catch {
          toast.error(t('share.error'));
        }
      }
    }
  };

  const handleSyncUserSeries = async () => {
    setSyncing(true);
    setSyncProgress(0);
    setSyncingCurrent(0);
    setSyncingTotal(0);
    setSyncingTitle(t('common.initializing'));

    const connection = watchlistAPI.syncUserTVShows({
      onProgress: (data) => {
        setSyncProgress(data.progress);
        setSyncingCurrent(data.current);
        setSyncingTotal(data.total);
        setSyncingTitle(data.title || t('common.initializing'));
      },
      onComplete: (data) => {
        setSyncing(false);
        setSyncProgress(100);
        if (data.success > 0) toast.success(t('settings.syncComplete'));
        if (data.failed > 0) toast.error(t('settings.syncError'));
        loadSeries();
      },
      onError: (data) => {
        setSyncing(false);
        toast.error(t('settings.syncError'));
      },
    });
    setEventSource(connection);
  };

  const cancelSync = () => {
    if (eventSource) eventSource.close();
    setSyncing(false);
    toast(t('common.cancel'));
  };

  const filteredSeries = series.filter((s) => {
    const progress = getProgress(s);
    const matchesFilter =
      filter === 'all' ||
      (filter === 'completed' && progress === 100) ||
      (filter === 'inProgress' && progress < 100);

    const matchesSearch =
      searchQuery.trim() === '' ||
      s.title.toLowerCase().includes(searchQuery.toLowerCase());

    // Hide completed filter
    const matchesHideCompleted = !hideCompleted || progress < 100;

    return matchesFilter && matchesSearch && matchesHideCompleted;
  });

  const sortedSeries = [...filteredSeries].sort((a, b) => {
    if (sortBy === 'date') {
      const dateA = new Date(a.added_at || 0);
      const dateB = new Date(b.added_at || 0);
      return dateSortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    } else if (sortBy === 'alphabetical') {
      const result = a.title.localeCompare(b.title, 'pt-BR');
      return alphabeticalSortDirection === 'asc' ? result : -result;
    } else if (sortBy === 'progress') {
      const progressA = getProgress(a);
      const progressB = getProgress(b);
      return progressSortDirection === 'desc' ? progressB - progressA : progressA - progressB;
    }
    return 0;
  });

  // Get filter label
  const getFilterLabel = () => {
    switch (filter) {
      case 'completed':
        return t('series.completed');
      case 'inProgress':
        return t('series.inProgress');
      default:
        return t('movies.all');
    }
  };

  // Get sort labels
  const getDateSortLabel = () => {
    return dateSortDirection === 'desc' ? 'Recente' : 'Antiga';
  };

  const getAlphaSortLabel = () => {
    return alphabeticalSortDirection === 'asc' ? 'A-Z' : 'Z-A';
  };

  const getProgressSortLabel = () => {
    return t('series.progress');
  };

  return (
    <div className="mobile-series" data-scroll-lock>
      {/* Header */}
      <div className="mobile-series__header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ margin: 0 }}>{t('series.mySeries')}</h1>
          <button
            className="mobile-series__sync-btn"
            onClick={handleSyncUserSeries}
            disabled={syncing}
            title={t('settings.syncTVShows')}
          >
            <HiOutlineRefresh size={16} className={syncing ? 'spinning' : ''} />
            <span>{t('common.update')}</span>
          </button>
        </div>
        <p>
          {series.length} {t('series.seriesInList')} • {fullyWatchedCount} {fullyWatchedCount === 1 ? t('series.seriesFullyWatchedSingular') : t('series.seriesFullyWatchedPlural')}
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '12px',
          marginTop: '8px'
        }}>
          <button
            className="mobile-series__pill mobile-series__pill--share"
            onClick={(e) => { e.stopPropagation(); handleShareList(); }}
          >
            <HiOutlineShare size={14} /> {t('share.shareList')}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label className="toggle-switch toggle-switch--medium">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-secondary)' }}>
              {t('series.hideCompleted')}
            </span>
          </div>
        </div>
      </div>

      {/* Sync Modal */}
      {syncing && (
        <div className="mobile-sync-modal">
          <div className="mobile-sync-modal__overlay" onClick={cancelSync} />
          <div className="mobile-sync-modal__content">
            <div className="mobile-sync-modal__icon">
              <HiOutlineRefresh size={32} className="spinning" />
            </div>
            <h3 className="mobile-sync-modal__title">{t('settings.syncing')}</h3>
            <p className="mobile-sync-modal__series">{syncingTitle}</p>
            
            <div className="mobile-sync-modal__progress-bar">
              <div 
                className="mobile-sync-modal__progress-fill"
                style={{ width: `${syncProgress}%` }}
              />
            </div>
            
            <div className="mobile-sync-modal__stats">
              <span>{syncingCurrent}/{syncingTotal}</span>
              <span>{syncProgress}%</span>
            </div>
            
            <button 
              className="mobile-sync-modal__cancel"
              onClick={cancelSync}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mobile-series__search-sync-row">
        <div className="mobile-series__search-input-wrapper">
          <HiOutlineSearch className="mobile-series__search-icon" size={20} />
          <input
            type="text"
            className="mobile-series__search-input"
            placeholder={t('series.searchSeries')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.length > 0 && (
            <button
              className="mobile-series__search-clear"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              <HiOutlineX size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Filter & Sort Pills */}
      <div className="mobile-series__pills-container">
        <div className="mobile-series__pills">
          <button
            className={`mobile-series__pill mobile-series__pill--date ${sortBy === 'date' ? 'active' : ''}`}
            onClick={() => {
              if (sortBy === 'date') {
                handleDateSort();
              } else {
                setSortBy('date');
              }
            }}
          >
            📅 {getDateSortLabel()}
          </button>
          <button 
            className={`mobile-series__pill mobile-series__pill--alpha ${sortBy === 'alphabetical' ? 'active' : ''}`}
            onClick={() => {
              if (sortBy === 'alphabetical') {
                handleAlphabeticalSort();
              } else {
                setSortBy('alphabetical');
              }
            }}
          >
            🔤 {getAlphaSortLabel()}
          </button>
          <button
            className={`mobile-series__pill mobile-series__pill--progress ${sortBy === 'progress' ? 'active' : ''}`}
            onClick={() => {
              if (sortBy === 'progress') {
                handleProgressSort();
              } else {
                setSortBy('progress');
              }
            }}
          >
            📊 {getProgressSortLabel()}
          </button>
        </div>
      </div>

      {/* Series List */}
      <div className="mobile-series__list">
        {loading ? (
          <div className="mobile-loading">
            <div className="spinner"></div>
          </div>
        ) : sortedSeries.length > 0 ? (
          sortedSeries.map((show) => {
            const progress = getProgress(show);
            return (
              <div 
                key={show.id} 
                className="mobile-series-card"
                onClick={() => navigate(`/series/${show.id}`)}
              >
                <div className="mobile-series-card__poster">
                  {show.poster_path ? (
                    <img
                      src={`${TMDB_IMAGE_BASE}${show.poster_path}`}
                      alt={show.title}
                    />
                  ) : (
                    <div className="mobile-series-card__poster-placeholder">
                      <HiOutlineVideoCamera size={32} />
                    </div>
                  )}
                  {progress === 100 && (
                    <div className="mobile-series-card__completed-badge">
                      ✓
                    </div>
                  )}
                </div>
                <div className="mobile-series-card__content">
                  <h3 className="mobile-series-card__title">{show.title}</h3>
                  <p className="mobile-series-card__meta">
                    {show.release_date
                      ? new Date(show.release_date).getFullYear()
                      : t('series.unknownYear')}
                    {show.release_date && (
                      show.status === 'Ended' || show.status === 'Canceled'
                        ? ` - ${show.last_air_date ? new Date(show.last_air_date).getFullYear() : ''}`
                        : ' - '
                    )}
                    {show.number_of_episodes > 0 && (
                      <span> &bull; {show.number_of_episodes} eps</span>
                    )}
                    {show.vote_average > 0 && (
                      <span> &bull; ⭐ {show.vote_average.toFixed(1)}</span>
                    )}
                  </p>
                  <div className="star-rating-with-value">
                    <RatingBottomSheet
                      rating={show.user_rating || 0}
                      onRate={(rating) => handleRate(show.id, rating)}
                      title={show.title}
                    />
                    {show.user_rating > 0 && (
                      <span className="star-rating-value">{show.user_rating} / 5</span>
                    )}
                  </div>
                  <div className="mobile-series-card__progress">
                    <div className="mobile-series-card__progress-bar">
                      <div 
                        className="mobile-series-card__progress-fill"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="mobile-series-card__progress-text">{progress}%</span>
                  </div>
                  <div className="mobile-series-card__actions">
                    <span className="mobile-series-card__view-btn">
                      {t('series.viewEpisodes')}
                      <HiOutlineChevronRight size={16} />
                    </span>
                    <button
                      className="mobile-series-card__btn-remove"
                      onClick={(e) => handleRemoveClick(show.id, e)}
                      title={t('series.remove')}
                    >
                      <HiOutlineTrash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="mobile-empty">
            <div className="mobile-empty__icon">
              <HiOutlineVideoCamera size={48} />
            </div>
            <h3>{t('series.noSeriesFound')}</h3>
            <p>{t('series.addSeriesFromSearch')}</p>
          </div>
        )}
      </div>

      {/* Bottom spacing for tab bar */}
      <div className="mobile-series__spacer" />

      {/* Confirm Remove Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={t('series.removeSeries')}
        message={t('series.confirmRemove')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={removeSeries}
        onCancel={() => setConfirmModal({ isOpen: false, seriesId: null, event: null })}
        type="danger"
      />
    </div>
  );
}

export default MobileSeries;
